//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { removeSwaggerUiControls, type OpenApiSpec } from '@hcengineering/text'

/**
 * Putting Swagger UI on the page, and keeping it to what it was told.
 *
 * One entry point for both places an OpenAPI block is drawn — the editor's node view and the
 * read-only viewer — so that the two cannot drift in what they permit. The package itself is
 * behind a dynamic import: it is a megabyte and a half of prebuilt bundle, and a page with no
 * OpenAPI block on it should not pay for it.
 *
 * The sweep below is the last of the four layers described in
 * file://./../../../../foundations/core/packages/text/src/openapi/swagger.ts. The first three
 * are configuration — an option, and component overrides — and each of them depends on being
 * right about Swagger UI's internals. This one does not: it looks at what actually ended up on
 * screen. In the ordinary case it matches nothing, every time.
 */

/** A mounted Swagger UI, and the way to take it off the page again. */
export interface SwaggerUiMount {
  /** Settles when Swagger UI is on screen; rejects if the package could not be loaded. */
  ready: Promise<void>
  destroy: () => void
}

/**
 * Draws `spec` into `container` and watches what is drawn.
 *
 * The specification must already have been through `parseOpenApiSpec`: what is handed over
 * here goes to Swagger UI unchanged, and the sanitising that makes that safe happens there.
 */
export function mountSwaggerUi (container: HTMLElement, spec: OpenApiSpec): SwaggerUiMount {
  let destroyed = false
  let observer: MutationObserver | undefined

  const sweep = (): void => {
    if (destroyed) return
    try {
      removeSwaggerUiControls(container)
    } catch {
      // Removing a node React still believes it owns can throw. Nothing matches in the
      // ordinary case, and a control left on screen is the thing worth avoiding here.
    }
  }

  const ready = (async () => {
    // Imported here rather than at the top of the file: this is what makes the bundle a
    // chunk of its own, fetched the first time a document actually contains one of these.
    const { createSwaggerUi } = await import('./swaggerUiRuntime')
    if (destroyed) return

    container.replaceChildren()
    createSwaggerUi(container, spec)

    if (destroyed) return

    sweep()

    // Swagger UI redraws itself whenever a section is expanded, so one sweep is not enough:
    // whatever it puts back is checked as it appears.
    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(sweep)
      observer.observe(container, { childList: true, subtree: true })
    }
  })()

  return {
    ready,
    destroy: () => {
      destroyed = true
      observer?.disconnect()
      observer = undefined
      container.replaceChildren()
    }
  }
}
